import type { CssVarsThemeOptions, Theme } from '@mui/joy/styles';
import { extendTheme as extendJoyTheme } from '@mui/joy/styles';
import { extendTheme as extendMuiTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';

declare module '@mui/joy/styles' {
  interface Shadow {
    subtle: string;
  }
}

declare module '@mui/joy/styles/types/zIndex' {
  interface DefaultZIndex {
    header: number;
    snackbar: number;
  }
}

const joyTheme = extendJoyTheme({
  zIndex: {
    header: 1100,
    snackbar: 1400,
  },
  shadow: {
    subtle: '0 0.125rem 1.25rem 0 rgba(0 0 0 / 5%), 0 0 0.25rem 0 rgba(0 0 0 / 10%)',
  },
  fontFamily: {
    body: 'var(--sd-typography-fontFamily)',
  },
  components: {
    JoyMenuList: {
      styleOverrides: {
        root: {
          '--List-padding': 'var(--xs)',
        },
      },
    },
    JoyRadio: {
      styleOverrides: {
        root: {
          '--Radio-action-radius': 0,
        },
      },
    },
  },
});

// Merge with MUI theme to support date pickers.
const muiTheme = extendMuiTheme({
  cssVarPrefix: 'joy',
});

const mergedTheme = {
  ...muiTheme,
  ...joyTheme,
  colorSchemes: deepmerge(joyTheme.colorSchemes, muiTheme.colorSchemes),
  typography: {
    ...muiTheme.typography,
    ...joyTheme.typography,
  },
} as unknown as Theme;

const theme: CssVarsThemeOptions = deepmerge(mergedTheme, {
  colorSchemes: {
    light: {
      palette: {
        primary: {
          50: 'var(--sd-color-primary-10)',
          100: 'var(--sd-color-primary-20)',
          200: 'var(--sd-color-primary-30)',
          300: 'var(--sd-color-primary-40)',
          400: 'var(--sd-color-primary-50)',
          500: 'var(--sd-color-primary-60)',
          600: 'var(--sd-color-primary-70)',
          700: 'var(--sd-color-primary-80)',
          800: 'var(--sd-color-primary-90)',
          900: 'var(--sd-color-primary-100)',
          solidBg: 'var(--joy-palette-primary-800)',
          solidHoverBg: 'var(--joy-palette-primary-700)',
          solidDisabledColor: 'var(--joy-palette-neutral-50)',
          solidDisabledBg: 'var(--joy-palette-neutral-300)',
          outlinedColor: 'var(--joy-palette-primary-800)',
          outlinedBorder: 'var(--joy-palette-primary-800)',
          outlinedHoverBg: 'var(--joy-palette-secondary-50)',
          outlinedHoverBorder: 'var(--joy-palette-primary-800)',
          outlinedDisabledColor: 'var(--joy-palette-neutral-400)',
          outlinedDisabledBorder: 'var(--joy-palette-neutral-400)',

          // Used by MUI X components. To be removed when MUI X supports Joy UI.
          main: 'var(--joy-palette-primary-700)',
          dark: 'var(--joy-palette-primary-800)',
          outlinedActiveBg: 'var(--joy-palette-secondary-200)',
        },
        secondary: {
          50: 'var(--sd-color-secondary-10)',
          100: 'var(--sd-color-secondary-20)',
          200: 'var(--sd-color-secondary-30)',
          300: 'var(--sd-color-secondary-40)',
          400: 'var(--sd-color-secondary-50)',
          500: 'var(--sd-color-secondary-60)',
          600: 'var(--sd-color-secondary-70)',
          700: 'var(--sd-color-secondary-80)',
          800: 'var(--sd-color-secondary-90)',
          900: 'var(--sd-color-secondary-100)',
        },
        neutral: {
          outlinedBorder: 'var(--joy-palette-neutral-500)',
          outlinedHoverBorder: 'var(--joy-palette-neutral-900)',
          plainHoverBg: 'var(--sd-color-hover-default)',
          plainActiveBg: 'var(--sd-color-active-default)',
        },
        focusVisible: 'var(--joy-palette-primary-700)',
        background: {
          body: '#fcfcfc',
          backdrop: 'hsl(0deg 0% 0% / 0.4)',
        },
        text: {
          secondary: 'var(--sd-typography-color-default)',
        },
      },
    },
  },
  // Used by MUI X components. To be removed when MUI X supports Joy UI.
  shape: {
    borderRadius: 0,
  },
  radius: {
    xs: '0px',
    sm: '0px',
    md: '0px',
    lg: '0px',
    xl: '0px',
  },
} as CssVarsThemeOptions);

export default theme;
